import { appPath } from '@fs/zion-config'
import { i18n } from '@fs/zion-locale'
import wcfThumb from '../../dlp/DiscoveryLandingPage/assets/tiles/wcf-thumb.svg'
import cafThumb from '../../dlp/DiscoveryLandingPage/assets/tiles/caf-thumb.svg'
import aamThumb from '../../dlp/DiscoveryLandingPage/assets/tiles/aam-thumb.svg'
import pmhThumb from '../../dlp/DiscoveryLandingPage/assets/tiles/pmh-thumb.svg'
import rmsThumb from '../../dlp/DiscoveryLandingPage/assets/tiles/rms-thumb.svg'
import mfrThumb from '../../dlp/DiscoveryLandingPage/assets/tiles/mfr-thumb.svg'
import ihaThumb from '../../dlp/DiscoveryLandingPage/assets/tiles/iha-thumb.svg'
import togThumb from '../../dlp/DiscoveryLandingPage/assets/tiles/tog-thumb.webp'
import { FAMILY_LINES_BASE_PATH } from '../../familylines/constants'
// Kiosk artwork (code-entry backgrounds and attract graphics) consumed
// via each experience's `kiosk` block — see ./kioskApps for the built kiosk APPS object.
import pohIcon from '../../poh/AppPoh/assets/POH-logo.svg'
import pohSplash from '../../poh/AppPoh/assets/POH-splash.jpg'
import aamBG from '../../aam/AppAam/images/aamCode.svg'
import rmsBG from '../../rms/AppRms/assets/rmsCode.svg'
import waifBG from '../../wcf/AppWcf/assets/waifCode.svg'
import pictureBG from '../../pmh/AppPmh/assets/pmhCode.svg'
import mfrBG from '../../mfc/assets/mfrCode.svg'
import cafBG from '../../caf2/images/cafCode.svg'
import allAboutMeAttractGraphic from '../../station/images/all-about-me-attract.svg'
import compareAFaceAttractGraphic from '../../station/images/compare-a-face-attract.svg'
import famousRelativesAttractGraphic from '../../station/images/famous-relatives-attract.svg'
import heritageExplorerAttractGraphic from '../../station/images/heritage-explorer-attract.svg'
import namesAttractGraphic from '../../station/images/names-attract.svg'
import timeMachineAttractGraphic from '../../station/images/time-machine-attract.svg'
import templeAttractGraphic from '../../station/images/temple-attract.svg'
import { TABLETOP_BACKGROUND } from '../../recordstable/styles/tabletop.styles'
import { HERITAGE_PORTRAITS_SURFACE_COLOR } from '../../heritageportraits/constants'

// Single source of truth for the Discovery experiences. Each surface selects and shapes
// records from this catalog: ./index.js builds the home/web/station cards, and
// ./kioskApps builds the kiosk APPS object from each record's `kiosk` block.
//
// i18n keys are the canonical, fully-localized dlp.*/emh.*/temple.* keys (NOT the
// English-only activitycenter.* keys), so every surface renders translated text.
// `cardAsset` is the CDN basename for Activity Center card artwork; `thumb` is the tile
// artwork used on the home page and for station cards without curated art. `kiosk` holds
// kiosk-only deployment + attract-screen config for experiences shown on a station.

const EXPERIENCES = [
  {
    id: 'together',
    titleKey: 'dlp.discovery-landing-page.tog.title',
    titleDefault: 'Together',
    descriptionKey: 'dlp.discovery-landing-page.tog.description',
    descriptionDefault: 'Bring your family closer—one story at a time.',
    web: { route: `${appPath}/together/stories`, guestRoute: `${appPath}/together`, external: true },
    thumb: togThumb,
    cardAsset: 'together',
    // surfaceColor is the featured card's fallback surface when the artwork doesn't load;
    // it must keep ≥4.5:1 contrast with textColor (WCAG AA — DSC-9370).
    featuredStyle: { textColor: '#ffffff', surfaceColor: '#33302a' },
  },

  {
    id: 'whereamifrom',
    appId: 'wcf',
    titleKey: 'dlp.discovery-landing-page.emh.title',
    titleDefault: 'Where am I from?',
    descriptionKey: 'dlp.discovery-landing-page.emh.description',
    descriptionDefault: 'Where in the world did you come from?',
    featuredPreHeadingKey: 'dlp.discovery-landing-page.emh.title',
    featuredPreHeadingDefault: 'Where am I from?',
    featuredHeadingKey: 'dlp.discovery-landing-page.emh.description',
    featuredHeadingDefault: 'Where in the world did you come from?',
    web: { route: '/explore', external: false },
    enableIf: () => i18n.language === 'en',
    thumb: wcfThumb,
    cardAsset: 'where-am-i-from',
    featuredStyle: { textColor: '#ffffff', surfaceColor: '#0d4d5e' },
    kiosk: {
      redirect: '/explore/generations',
      unauthRedirect: '/explore/heritage',
      bg: waifBG,
      gradientColor1: '#DBF0F7',
      gradientColor2: '#5BC5DE',
      guestModeAllowed: true,
      primaryTextColor: '#00365D',
      secondaryTextColor: '#066F90',
      allowChineseSurnameExperience: true,
      codeEntryAttract: {
        background: '#215b66',
        graphic: heritageExplorerAttractGraphic,
        graphicSize: 'min(72vh, 52vw) auto',
      },
    },
  },
  {
    id: 'allaboutme',
    appId: 'aam',
    titleKey: 'dlp.discovery-landing-page.aam.title',
    titleDefault: 'All About Me',
    descriptionKey: 'dlp.discovery-landing-page.aam.description',
    descriptionDefault: 'You are one of a kind! Learn more about yourself and your family.',
    web: { route: '/about', external: false },
    thumb: aamThumb,
    // Reuses the My Year card artwork — there's no dedicated All About Me card image.
    cardAsset: 'my-year',
    kiosk: {
      redirect: '/about',
      unauthRedirect: '/about',
      bg: aamBG,
      gradientColor1: '#FEE6E2',
      gradientColor2: '#F87895',
      guestModeAllowed: true,
      primaryTextColor: '#6B062D',
      secondaryTextColor: '#C0285C',
      allowChineseSurnameExperience: true,
      codeEntryAttract: {
        background: '#0d8f99',
        graphic: allAboutMeAttractGraphic,
        graphicSize: 'min(77vh, 58vw) auto',
      },
    },
  },
  {
    id: 'famousrelatives',
    appId: 'mfr',
    titleKey: 'dlp.discovery-landing-page.mfr.title',
    titleDefault: 'Famous Relatives',
    descriptionKey: 'dlp.discovery-landing-page.mfr.description',
    descriptionDefault: 'Your family might be kinda famous!',
    web: { route: '/famousrelatives', external: false },
    thumb: mfrThumb,
    cardAsset: 'famous-relatives',
    kiosk: {
      redirect: '/famousrelatives',
      unauthRedirect: '/famousrelatives/guest',
      bg: mfrBG,
      gradientColor1: '#FAECB4',
      gradientColor2: '#F9C636',
      guestModeAllowed: false,
      primaryTextColor: '#621C00',
      secondaryTextColor: '#B14402',
      allowChineseSurnameExperience: true,
      codeEntryAttract: {
        background: '#cf3a61',
        graphic: famousRelativesAttractGraphic,
        graphicSize: 'min(85vh, 60vw) auto',
      },
    },
  },
  {
    id: 'compareaface',
    appId: 'caf',
    // Mirror the featured carousel's two lines: the Compare-A-Face brand as the title and the
    // "Discover the ancestor…" tagline as the subtext (reuses the already-localized keys below).
    titleKey: 'landing.apps-links.caf.title',
    titleDefault: 'Compare-A-Face',
    descriptionKey: 'dlp.discovery-landing-page.caf.title',
    descriptionDefault: 'Discover the ancestor you resemble most.',
    featuredPreHeadingKey: 'landing.apps-links.caf.title',
    featuredPreHeadingDefault: 'Compare-A-Face',
    featuredHeadingKey: 'dlp.discovery-landing-page.caf.title',
    featuredHeadingDefault: 'Discover the ancestor you resemble most.',
    web: { route: '/compareme', external: false },
    thumb: cafThumb,
    cardAsset: 'compare-a-face',
    featuredStyle: { textColor: '#2b2b2b' },
    kiosk: {
      redirect: '/compareme',
      unauthRedirect: '/compareme',
      bg: cafBG,
      gradientColor1: '#F3E9FA',
      gradientColor2: '#D0A8F3',
      guestModeAllowed: true,
      primaryTextColor: '#4D1577',
      secondaryTextColor: '#8C3FCC',
      allowChineseSurnameExperience: false,
      codeEntryAttract: {
        background: '#236138',
        graphic: compareAFaceAttractGraphic,
        graphicSize: 'min(60vh, 50vw) auto',
      },
    },
  },
  {
    id: 'recordmystory',
    appId: 'rms',
    titleKey: 'dlp.discovery-landing-page.rms.title',
    titleDefault: 'Record My Story',
    descriptionKey: 'dlp.discovery-landing-page.rms.description',
    descriptionDefault: 'Answer a few questions. Leave a legacy.',
    web: { route: '/recordme', external: false },
    cardAsset: 'record-my-story',
    thumb: rmsThumb,
    kiosk: {
      redirect: '/recordme',
      unauthRedirect: '/recordme',
      bg: rmsBG,
      gradientColor1: '#FAECB4',
      gradientColor2: '#F9C636',
      guestModeAllowed: true,
      primaryTextColor: '#621C00',
      secondaryTextColor: '#B14402',
      allowChineseSurnameExperience: false,
      codeEntryAttract: {
        background: '#B14402',
        graphic: heritageExplorerAttractGraphic,
        graphicSize: 'min(72vh, 52vw) auto',
      },
    },
  },
  {
    // The currently-built, live PMH experience at /picture. Uses the old card artwork
    // (`picture-my-heritage-old`); the `picture-my-heritage` art belongs to the new
    // experience below.
    id: 'picturemyheritage-old',
    appId: 'pmh',
    cardAsset: 'picture-my-heritage-old',
    featuredStyle: { textColor: '#ffffff', surfaceColor: '#2f4b22' },
    titleKey: 'dlp.discovery-landing-page.pmh.title',
    titleDefault: 'Picture My Heritage',
    descriptionKey: 'dlp.discovery-landing-page.pmh.description',
    descriptionDefault: 'Picture yourself from a different time and place.',
    web: { route: '/picture', external: false },
    // http://www.openradar.me/33571214
    enableIf: () => !!navigator?.mediaDevices?.getUserMedia,
    thumb: pmhThumb,
    kiosk: {
      redirect: '/picture',
      unauthRedirect: '/picture',
      bg: pictureBG,
      gradientColor1: '#EEEFC9',
      gradientColor2: '#C6D64E',
      guestModeAllowed: true,
      primaryTextColor: '#023D18',
      secondaryTextColor: '#367419',
      allowChineseSurnameExperience: false,
      codeEntryAttract: { background: '#559b34', graphicSize: 'min(85vh, 60vw) auto' },
    },
  },
  {
    // The new web-only PMH experience. The legacy /picture experience above remains
    // independently available to home and station surfaces.
    id: 'picturemyheritage',
    appId: 'pmh',
    cardAsset: 'picture-my-heritage',
    featuredStyle: { textColor: '#ffffff' },
    titleKey: 'dlp.discovery-landing-page.pmh.title',
    titleDefault: 'Picture My Heritage',
    descriptionKey: 'dlp.discovery-landing-page.pmh.description',
    descriptionDefault: 'Picture yourself from a different time and place.',
    web: { route: '/picturemyheritage', external: false },
    thumb: pmhThumb,
  },
  {
    id: 'inhomeactivities',
    cardAsset: 'in-home',
    titleKey: 'dlp.discovery-landing-page.in-home-activities.title',
    titleDefault: 'In-Home Activities',
    descriptionKey: 'dlp.discovery-landing-page.in-home-activities.description',
    descriptionDefault: 'Explore your family history together.',
    web: { route: `${appPath}/activities/`, external: true },
    thumb: ihaThumb,
  },
  {
    id: 'mytimemachine',
    appId: 'mytimemachine',
    prototypeOnly: true,
    comingSoon: true,
    titleKey: 'dlp.discovery-landing-page.mtm.title',
    titleDefault: 'Time Machine',
    descriptionKey: 'dlp.discovery-landing-page.mtm.description',
    descriptionDefault: "See history through your ancestor's eyes.",
    featuredPreHeadingKey: 'landing-page.mtm.heading',
    featuredPreHeadingDefault: 'My Time Machine',
    featuredHeadingKey: 'dlp.discovery-landing-page.mtm.description',
    featuredHeadingDefault: "See history through your ancestor's eyes.",
    web: { route: '/mytimemachine', external: false },
    cardAsset: 'time-machine',
    featuredStyle: { textColor: '#2b2b2b' },
    kiosk: {
      redirect: '/mytimemachine',
      unauthRedirect: '',
      bg: '',
      guestModeAllowed: false,
      allowChineseSurnameExperience: true,
      codeEntryAttract: {
        background: '#b78c18',
        graphic: timeMachineAttractGraphic,
        graphicSize: 'min(70vh, 55vw) auto',
      },
    },
  },
  {
    id: 'heritage',
    appId: 'heritageexplorer',
    titleKey: 'dlp.discovery-landing-page.myheritage.title',
    titleDefault: 'Heritage Explorer',
    // Card-specific key so the activity card copy is independent of the MyHeritage landing page.
    descriptionKey: 'dlp.discovery-landing-page.myheritage.description',
    descriptionDefault: 'Explore your ancestral homelands.',
    web: { route: '/heritage', external: false },
    cardAsset: 'heritage-explorer',
    featuredStyle: { textColor: '#ffffff' },
    kiosk: {
      redirect: '/heritage',
      unauthRedirect: '/heritage',
      bg: '',
      guestModeAllowed: true,
      allowChineseSurnameExperience: false,
      codeEntryAttract: {
        background: '#215b66',
        graphic: heritageExplorerAttractGraphic,
        graphicSize: 'min(72vh, 52vw) auto',
      },
    },
  },
  {
    id: 'temple',
    appId: 'temple',
    prototypeOnly: true,
    comingSoon: true,
    titleKey: 'temple.landingpage.title',
    titleDefault: 'Temple Explorer',
    descriptionKey: 'temple.landingpage.subtitle',
    descriptionDefault: 'Sacred places. Eternal connections.',
    web: { route: '/saltlakecitytemple', external: false },
    cardAsset: 'temple-explorer',
    kiosk: {
      redirect: '/saltlakecitytemple',
      unauthRedirect: '/saltlakecitytemple',
      guestModeAllowed: true,
      allowChineseSurnameExperience: false,
      codeEntryAttract: {
        background: '#E7E6DF',
        graphic: templeAttractGraphic,
        graphicSize: 'min(70vh, 39vw) auto',
        darkMode: true,
        accentTextColor: '#AE841E',
        logoColor: '#646261',
      },
    },
  },
  {
    // Prototype-only until design sign-off. Not station-eligible, but its `kiosk` block makes it a
    // code-entry app: /kioskcodeentry?app=heritageportraits redeems a patron's code and lands them
    // in the experience, which is also where the experience sends a visitor with no Session.
    // Reuses the PMH thumb - a `cardAsset` would 404 against the CDN until art exists.
    // No `requiresFlag`: since DSC-10994 the route is gated by discovery_prototypes alone,
    // which is the same flag `prototypeOnly` checks, so the card and the route agree.
    id: 'heritageportraits',
    appId: 'heritageportraits',
    prototypeOnly: true,
    titleKey: 'heritageportraits.catalog.title',
    titleDefault: 'Heritage Portraits',
    descriptionKey: 'heritageportraits.catalog.description',
    descriptionDefault: "See what you'd look like 100 years ago in the places your family came from.",
    web: { route: '/heritageportraits', external: false },
    thumb: pmhThumb,
    kiosk: {
      redirect: '/heritageportraits',
      // A guest's code carries a Session (and its allowance) just like a signed-in patron's, so
      // both land in the experience. There is still no "Use without a code" button
      // (allowGuestActivation): without a code there is no Session, and the experience would
      // send the patron straight back here.
      unauthRedirect: '/heritageportraits',
      guestModeAllowed: true,
      // The legacy attract screen only renders with discovery_prototypes off, and then
      // /heritageportraits is not routed at all.
      bg: '',
      allowChineseSurnameExperience: false,
      // The experience's own surface, so the attract screen, the phone landing page behind its QR
      // code and the experience read as one place. No attract graphic until station art exists.
      codeEntryAttract: { background: HERITAGE_PORTRAITS_SURFACE_COLOR },
    },
  },
  {
    // WAIF's My Family Lines tab, as its own labs experience (DSC-10385).
    // prototype-only + comingSoon like the other in-progress labs experiences: a teaser on
    // /new until discovery_prototypes is on. No kiosk block yet - stations keep reaching
    // Family Lines through WAIF. The tile thumb is WAIF's until the redesign ships its own art.
    id: 'familylines',
    appId: 'familylines',
    prototypeOnly: true,
    comingSoon: true,
    titleKey: 'dlp.discovery-landing-page.familylines.title',
    titleDefault: 'Family Lines',
    descriptionKey: 'dlp.discovery-landing-page.familylines.description',
    descriptionDefault: 'Follow your family across the world.',
    web: { route: FAMILY_LINES_BASE_PATH, external: false },
    thumb: wcfThumb,
  },
  {
    id: 'myname',
    appId: 'myname',
    titleKey: 'dlp.discovery-landing-page.myname.title',
    titleDefault: 'My Name',
    descriptionKey: 'dlp.discovery-landing-page.myname.description',
    descriptionDefault: 'Explore the history and meaning of your name.',
    web: { route: '/myname', external: false },
    cardAsset: 'my-name',
    featuredStyle: { textColor: '#FFFFFF' },
    kiosk: {
      redirect: '/myname',
      unauthRedirect: '/myname',
      bg: '',
      guestModeAllowed: true,
      allowChineseSurnameExperience: false,
      codeEntryAttract: { background: '#573b88', graphic: namesAttractGraphic, graphicSize: 'min(75vh, 55vw) auto' },
    },
  },
  {
    id: 'myyear',
    appId: 'myyear',
    titleKey: 'dlp.discovery-landing-page.myyear.title',
    titleDefault: 'My Year',
    descriptionKey: 'dlp.discovery-landing-page.myyear.description',
    descriptionDefault: 'Rediscover the year you were born.',
    web: { route: '/myyear', external: false },
    thumb: aamThumb,
    cardAsset: 'my-year',
    featuredStyle: { textColor: '#FFFFFF' },
    kiosk: {
      redirect: '/myyear',
      unauthRedirect: '/myyear',
      bg: aamBG,
      gradientColor1: '',
      gradientColor2: '',
      guestModeAllowed: true,
      primaryTextColor: '',
      secondaryTextColor: '',
      allowChineseSurnameExperience: true,
      codeEntryAttract: {
        background: '#0d8f99',
        graphic: allAboutMeAttractGraphic,
        graphicSize: 'min(77vh, 58vw) auto',
      },
    },
  },
  {
    // Famous Relatives (My Famous Relatives) — active in labs: the card always shows and the
    // /myfamousrelatives route is served by the mfr2 FamousRelatives app whenever either
    // fslabs_discovery_new or discovery_prototypes is on (see DsApp.js, DSC-10047).

    id: 'myfamousrelatives',
    appId: 'myfamousrelatives',
    titleKey: 'dlp.discovery-landing-page.mfr.title',
    titleDefault: 'Famous Relatives',
    descriptionKey: 'dlp.discovery-landing-page.mfr.description',
    descriptionDefault: 'Your family might be kinda famous!',
    web: { route: '/myfamousrelatives', external: false },
    thumb: mfrThumb,
    cardAsset: 'famous-relatives',
    kiosk: {
      redirect: '/myfamousrelatives',
      unauthRedirect: '/myfamousrelatives/guest',
      bg: mfrBG,
      gradientColor1: '#FAECB4',
      gradientColor2: '#F9C636',
      guestModeAllowed: false,
      primaryTextColor: '#621C00',
      secondaryTextColor: '#B14402',
      allowChineseSurnameExperience: true,
      codeEntryAttract: {
        background: '#cf3a61',
        graphic: famousRelativesAttractGraphic,
        graphicSize: 'min(85vh, 60vw) auto',
      },
    },
  },

  {
    // Kiosk experience: appears on stations. Has no discovery card, but is station-eligible
    // (see STATION_ELIGIBLE in ./lineups) so it needs a station thumb/card image — reuses the
    // hero art from its own landing page (PohLandingPage) rather than a dedicated web asset.
    // Stopgap: every other station experience uses CDN card artwork via `cardAsset`, but no
    // picture-our-heritage card asset has been published yet (DSC-10491).
    id: 'pictureourheritage',
    appId: 'poh',
    titleKey: 'dlp.discovery-landing-page.poh.title',
    titleDefault: 'Picture Our Heritage',
    descriptionKey: 'dlp.discovery-landing-page.poh.description',
    descriptionDefault: 'Connect to your heritage. Add yourselves to photos from around the world.',
    thumb: pohSplash,
    kiosk: {
      redirect: '/pictureour',
      unauthRedirect: '/pictureour',
      icon: pohIcon,
      name: 'Picture Our Heritage',
      background: '#6bb344',
      bg: pictureBG,
      gradientColor1: '#EEEFC9',
      gradientColor2: '#C6D64E',
      primaryTextColor: '#023D18',
      guestModeAllowed: true,
      allowGuestActivation: true,
      secondaryTextColor: '#367419',
      allowChineseSurnameExperience: false,
      codeEntryAttract: { background: '#559b34' },
    },
  },
  // No lineup entry (HOME/LABS_WEB/STATION_ELIGIBLE): on the web it is reachable by
  // direct URL behind discovery_prototypes, and on a kiosk by code entry at
  // /kioskcodeentry?app=recordstable. The /selectexperience lineup waits on station
  // card artwork - STATION_ELIGIBLE requires a cardAsset or a thumb, and this
  // experience has neither yet (DSC-10480).
  {
    id: 'recordstable',
    appId: 'recordstable',
    titleKey: 'dlp.discovery-landing-page.recordstable.title',
    titleDefault: 'Records Table',
    descriptionKey: 'dlp.discovery-landing-page.recordstable.description',
    descriptionDefault: 'Spread your ancestors’ historical records across a table.',
    prototypeOnly: true,
    web: { route: '/recordstable', external: false },
    kiosk: {
      redirect: '/recordstable',
      // No guest route, and no guest activation button: the table is built from the
      // patron's own ancestors, so a guest session has nothing to lay out. A code is
      // the point, the same way it is for My Time Machine.
      unauthRedirect: '',
      guestModeAllowed: false,
      // The legacy attract screen's gradient and background artwork are deliberately
      // absent: it renders only while discovery_prototypes is off, and with the flag
      // off /recordstable is not routed at all.
      bg: '',
      allowChineseSurnameExperience: true,
      codeEntryAttract: {
        // The tabletop itself, so the attract screen, the phone landing page the QR
        // code leads to (see resolveLoginTheme) and the experience are one surface.
        // White billboard text on it measures 7.8:1.
        background: TABLETOP_BACKGROUND,
        // No attract graphic yet - the station artwork is commissioned per experience,
        // and a missing one renders as background-image: none rather than a broken
        // tile (same as the personal video station in ./kioskApps).
      },
    },
  },
]

export const catalog = EXPERIENCES.reduce((acc, experience) => {
  acc[experience.id] = experience
  return acc
}, {})

export default catalog
